import React from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Popover, Tag } from 'antd';
import { Eye, ThreeDotsVertical, Pencil, Trash, ArrowCounterclockwise } from 'react-bootstrap-icons';
import type { Basket } from '../../types/basket';
import TableComponent from '../common/TableComponent';
import { ActionMenu } from '../common/ActionMenu';
import dayjs from 'dayjs';
import { PermissionEnum } from '../../enums/PermissionEnum';
import { usePermissions } from '../../hooks/usePermissions';
import { ActiveFilterEnum } from '../../enums/ActiveFilterEnum';

interface BasketTableProps {
  baskets: Basket[];
  loading?: boolean;
  isOnline: boolean;
  totalBaskets: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, size?: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onViewProductAssignments: (id: number) => void;
  onRestore?: (id: number) => void;
  statusFilter?: ActiveFilterEnum;
}

type BasketMenuItem = {
  text: string;
  icon: React.ReactNode;
  click: () => void;
  disabled: boolean;
  description?: string;
};
const getMainProducts = (basket: Basket) => basket.mainProducts ?? [];

const BasketTable: React.FC<BasketTableProps> = ({
  baskets,
  loading = false,
  isOnline,
  totalBaskets,
  currentPage,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onViewProductAssignments,
  onRestore,
  statusFilter = ActiveFilterEnum.ACTIVE,
}) => {
  const { t } = useTranslation(['basket', 'common']);
  const { hasPermission } = usePermissions();
  const canManageBaskets = hasPermission(PermissionEnum.MANAGE_BASKETS);

  const getBasketColumns = () => [
    {
      title: t('basketNameColumn'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      width: 200,
    },
    {
      title: t('noOfProducts'),
      dataIndex: 'productCount',
      key: 'productCount',
      width: 100,
      align: 'center' as const,
      render: (_: number, record: Basket) => {
        const mainProducts = getMainProducts(record);
        const productCount = mainProducts.length;

        if (productCount === 0) {
          return <span className="text-gray-600">N/A</span>;
        }

        return (
          <Popover
            content={
              <div className="max-h-[180px] max-w-xs space-y-1 overflow-auto p-1">
                {mainProducts.length > 0 ? (
                  mainProducts.map((mainProduct, index) => (
                    <div key={`${mainProduct.id}-${index}`} className="text-sm text-gray-700">
                      {'\u2022'} {mainProduct.name || mainProduct.id}
                    </div>
                  ))
                ) : (
                  <span className="text-sm text-gray-600">N/A</span>
                )}
              </div>
            }
            title={t('mainProducts')}
            trigger="click"
            placement="top"
          >
            <Tag className="cursor-pointer transition-colors hover:bg-blue-100" color="blue">
              {productCount}
            </Tag>
          </Popover>
        );
      },
    },
    {
      title: t('created'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 120,
      align: 'center' as const,
      render: (createTime: string | number | null) => (
        <span className="text-sm text-gray-600">
          {' '}
          {dayjs(Number(createTime)).format('YYYY-MM-DD')}
        </span>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'isActive',
      key: 'status',
      width: 120,
      align: 'center' as const,
      render: (isActive: boolean) => (
        <Tag
          color={isActive !== false ? 'green' : 'red'}
        >
          {isActive !== false ? t('common:statusFilter.active') : t('common:statusFilter.inactive')}
        </Tag>
      ),
    },
    ...[
      {
        title: t('action'),
        dataIndex: 'action',
        key: 'action',
        width: 100,
        align: 'center' as const,
        render: (_: unknown, record: Basket) => {
          const isBaseActionDisabled = !record?.id;
          const mutationActionDisabled = isBaseActionDisabled || !isOnline;
          const disabledDescription = isBaseActionDisabled ? t('notSynced') : undefined;
          const editDisabledDescription = isBaseActionDisabled
            ? disabledDescription
            : !isOnline
              ? t('offlineEditInfoTitle')
              : undefined;
          const deleteDisabledDescription = isBaseActionDisabled
            ? disabledDescription
            : !isOnline
              ? t('offlineDeleteInfoTitle')
              : undefined;
          const menuItems: BasketMenuItem[] = [
            {
              text: t('viewProductAssignments'),
              icon: <Eye size={16} className="text-primary-blue-icon" />,
              click: () => onViewProductAssignments(record.id),
              disabled: isBaseActionDisabled,
              description: disabledDescription,
            },
            ...(canManageBaskets && record.isActive !== false
              ? [
                {
                  text: t('edit'),
                  icon: <Pencil size={16} className="text-primary-blue-icon" />,
                  click: () => onEdit(record.id),
                  disabled: mutationActionDisabled,
                  description: editDisabledDescription,
                },
                {
                  text: t('delete'),
                  icon: <Trash size={16} className="text-primary-blue-icon" />,
                  click: () => onDelete(record.id),
                  disabled: mutationActionDisabled,
                  description: deleteDisabledDescription,
                },
              ]
              : []),
            ...(canManageBaskets && record.isActive === false && onRestore
              ? [
                {
                  text: t('restore'),
                  icon: <ArrowCounterclockwise size={16} className="text-primary-blue-icon" />,
                  click: () => onRestore(record.id),
                  disabled: mutationActionDisabled,
                  description: disabledDescription,
                },
              ]
              : []),
          ];

          return (
            <Popover
              content={<ActionMenu menuItems={menuItems} />}
              className="cursor-pointer"
              placement="bottom"
              trigger="hover"
              overlayStyle={{ zIndex: 800 }}
            >
              <Row justify="center">
                <ThreeDotsVertical size={15} />
              </Row>
            </Popover>
          );
        },
      },
    ],
  ];

  return (
    <div className="mt-6 w-full overflow-x-auto">
      <TableComponent
        tableData={baskets}
        pageSize={pageSize}
        currentPage={currentPage}
        totalItems={totalBaskets}
        onPaginationChanged={onPageChange}
        columns={getBasketColumns()}
        loading={loading}
        showHeader
      />
    </div>
  );
};

export default BasketTable;
